import { SearchBar } from '@/components/forms/search-bar';
import { QueryState } from '@/components/layout/query-state';
import { ScreenHeader } from '@/components/layout/screen-header';
import { PageTitle } from '@/components/seo/page-title';
import { Text } from '@/components/ui/text';
import { useAppTheme } from '@/providers/theme-provider';
import { router } from 'expo-router';
import { useCallback } from 'react';
import { ActivityIndicator, FlatList, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { BusinessResultItem } from './components/business-result-item';
import { MenuItemResultItem } from './components/menu-item-result-item';
import { SearchEmptyState } from './components/search-empty-state';
import { SearchTabBar } from './components/search-tab-bar';
import { UserResultItem } from './components/user-result-item';
import { useSearchResults } from './hooks/use-search-results';
import { useSearchState } from './hooks/use-search-state';

export function SearchScreen() {
  const theme = useAppTheme();
  const state = useSearchState();
  const results = useSearchResults({
    debouncedQuery: state.debouncedQuery,
    activeTab: state.activeTab,
  });

  const trimmed = state.debouncedQuery.trim();
  const minLength = state.activeTab === 'menu-items' ? 1 : 2;
  const hasSearched = trimmed.length >= minLength && results.totalResults > 0;

  const handleBusinessPress = useCallback((id: string) => {
    router.push({ pathname: '/(root)/business/[id]', params: { id } });
  }, []);

  const handleUserPress = useCallback((identifier: string) => {
    router.push({ pathname: '/(root)/profile/[username]', params: { username: identifier } });
  }, []);

  const handleMenuItemPress = useCallback((id: string) => {
    router.push({ pathname: '/(root)/menu-item/[id]', params: { id } });
  }, []);

  const handleLoadMore = useCallback(() => {
    if (results.hasNextPage && !results.isFetchingNextPage) {
      results.fetchNextPage();
    }
  }, [results.hasNextPage, results.isFetchingNextPage, results.fetchNextPage]);

  const showInitialPrompt = trimmed.length < minLength && !results.isLoading;

  return (
    <SafeAreaView className="flex-1" edges={['top']}>
      <PageTitle title="Search" />
      <ScreenHeader title="Search" showBackButton />

      <View className="px-4 pt-4 pb-2">
        <SearchBar
          value={state.searchQuery}
          onChangeText={state.handleSearchChange}
          placeholder="Search restaurants, dishes, or users"
          onSubmit={state.handleSearchSubmit}
        />
      </View>

      <SearchTabBar activeTab={state.activeTab} onTabChange={state.setActiveTab} />

      {showInitialPrompt ? (
        <SearchEmptyState variant="initial-prompt" activeTab={state.activeTab} query="" />
      ) : (
        <QueryState
          isLoading={results.isLoading && !results.isFetchingNextPage}
          error={results.error}
          onRetry={() => results.refetch()}
          loadingMessage="Searching..."
          fullScreen={false}
        >
          <View className="flex-1 px-4">
            {results.totalResults > 0 && (
              <Text className="text-sm mb-3 text-muted-foreground">
                {results.totalResults} {results.totalResults === 1 ? 'result' : 'results'} found
              </Text>
            )}

            {state.activeTab === 'places' && (
              <FlatList
                data={results.businesses}
                renderItem={({ item }) => <BusinessResultItem business={item} onPress={handleBusinessPress} />}
                keyExtractor={(item) => item.id}
                ListEmptyComponent={<SearchEmptyState variant="no-results" activeTab={state.activeTab} query={trimmed} />}
                ListFooterComponent={results.isFetchingNextPage ? <ActivityIndicator size="small" color={theme.colors.primary} className="py-4" /> : null}
                onEndReached={handleLoadMore}
                onEndReachedThreshold={0.5}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 20 }}
              />
            )}

            {state.activeTab === 'people' && (
              <FlatList
                data={results.users}
                renderItem={({ item }) => <UserResultItem user={item} onPress={handleUserPress} />}
                keyExtractor={(item) => item.id}
                ListEmptyComponent={<SearchEmptyState variant="no-results" activeTab={state.activeTab} query={trimmed} />}
                ListFooterComponent={results.isFetchingNextPage ? <ActivityIndicator size="small" color={theme.colors.primary} className="py-4" /> : null}
                onEndReached={handleLoadMore}
                onEndReachedThreshold={0.5}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 20 }}
              />
            )}

            {state.activeTab === 'menu-items' && (
              <FlatList
                data={results.menuItems}
                renderItem={({ item }) => <MenuItemResultItem menuItem={item} onPress={handleMenuItemPress} />}
                keyExtractor={(item) => item.id}
                ListEmptyComponent={<SearchEmptyState variant="no-results" activeTab={state.activeTab} query={trimmed} />}
                ListFooterComponent={results.isFetchingNextPage ? <ActivityIndicator size="small" color={theme.colors.primary} className="py-4" /> : null}
                onEndReached={handleLoadMore}
                onEndReachedThreshold={0.5}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 20 }}
              />
            )}
          </View>
        </QueryState>
      )}
    </SafeAreaView>
  );
}
